import { Controller } from "@hotwired/stimulus"

// Action types that need no value input — submit hidden value="1"
const NO_VALUE_ACTIONS = new Set(['mark_read', 'archive', 'block_sender'])

export default class extends Controller {
  static targets = ["conditionsList", "actionsList", "conditionTemplate", "actionTemplate"]
  static values = {
    folders:    Array,  // [{ id, name }]
    tags:       Array,  // [{ id, tag }]
    people:     Array,  // [{ id, name }]
    priorities: Array   // [{ id, name }]
  }

  connect() {
    // Initialise value pickers for any pre-existing rows (edit form)
    this.actionsListTarget.querySelectorAll('[data-action-row]').forEach(row => {
      const select = row.querySelector('select[name*="[type]"]')
      if (select?.value) this._applyValueWidget(row, select.value, select.value)
    })
  }

  // ── Conditions ──────────────────────────────────────────────────────────────

  addCondition() {
    const template = this.conditionTemplateTarget.content.cloneNode(true)
    const index = this.conditionsListTarget.children.length
    template.querySelectorAll('[name]').forEach(el => {
      el.name = el.name.replace('INDEX', index)
    })
    this.conditionsListTarget.appendChild(template)
  }

  removeCondition(event) {
    event.currentTarget.closest('[data-condition-row]').remove()
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  addAction() {
    const template = this.actionTemplateTarget.content.cloneNode(true)
    const index = this.actionsListTarget.children.length
    template.querySelectorAll('[name]').forEach(el => {
      el.name = el.name.replace('INDEX', index)
    })
    this.actionsListTarget.appendChild(template)
  }

  removeAction(event) {
    event.currentTarget.closest('[data-action-row]').remove()
  }

  // Called when an action type <select> changes
  updateActionValue(event) {
    const select    = event.currentTarget
    const row       = select.closest('[data-action-row]')
    const actionType = select.value
    this._applyValueWidget(row, actionType, null)
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  // Replaces the value container widget inside `row` based on `actionType`.
  // `existingValue` is null when called from a change event (fresh widget),
  // or the current persisted value string when called from connect().
  _applyValueWidget(row, actionType, existingValue) {
    const container = row.querySelector('[data-action-value-container]')
    if (!container) return

    // Derive the field name from any existing named element inside the container
    const existing = container.querySelector('[name]')
    const baseName = existing
      ? existing.name.replace(/\[value\]$/, '[value]')
      : container.dataset.valueName || ''

    const value = existingValue ?? ''

    container.innerHTML = ''

    if (NO_VALUE_ACTIONS.has(actionType)) {
      // No visible input — just submit value="1"
      const hidden = document.createElement('input')
      hidden.type  = 'hidden'
      hidden.name  = baseName
      hidden.value = '1'
      container.appendChild(hidden)
      return
    }

    let el

    switch (actionType) {
      case 'move_to_folder':
        el = this._buildSelect(baseName, this.foldersValue, 'id', 'name', value)
        break

      case 'add_tag':
        el = this._buildSelect(baseName, this.tagsValue, 'id', 'tag', value)
        break

      case 'assign_responsible':
        el = this._buildSelect(baseName, this.peopleValue, 'id', 'name', value)
        break

      case 'set_priority':
        el = this._buildSelect(baseName, this.prioritiesValue, 'id', 'name', value)
        break

      case 'auto_reply': {
        const ta = document.createElement('textarea')
        ta.name        = baseName
        ta.className   = 'form-control form-control-sm'
        ta.rows        = 3
        ta.textContent = value
        el = ta
        break
      }

      case 'forward_to': {
        const inp = document.createElement('input')
        inp.type        = 'email'
        inp.name        = baseName
        inp.className   = 'form-control form-control-sm'
        inp.value       = value
        inp.placeholder = 'email@example.com'
        el = inp
        break
      }

      default: {
        // Fallback: plain text input (handles empty/unknown action type)
        const inp = document.createElement('input')
        inp.type        = 'text'
        inp.name        = baseName
        inp.className   = 'form-control form-control-sm'
        inp.value       = value
        inp.placeholder = this.element.dataset.emailRuleBuilderActionPlaceholder || ''
        el = inp
        break
      }
    }

    container.appendChild(el)
  }

  _buildSelect(name, options, valueKey, labelKey, selectedValue) {
    const sel = document.createElement('select')
    sel.name      = name
    sel.className = 'form-select form-select-sm'

    options.forEach(opt => {
      const o       = document.createElement('option')
      o.value       = opt[valueKey]
      o.textContent = opt[labelKey]
      if (String(opt[valueKey]) === String(selectedValue)) o.selected = true
      sel.appendChild(o)
    })

    return sel
  }
}
